/**
 * Convert DRBFM Failure Modes to Verification Items Dialog
 *
 * 3-step wizard dialog:
 * 1. Select failure modes to convert
 * 2. Choose target verification session/unit
 * 3. Preview generated items and confirm
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPlus, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Stepper } from '@/components/ui/stepper';
import { cn } from '@/lib/utils';
import { useConfirm } from '@hooks/useConfirm';
import { FailureMode, DesignChange, Countermeasure } from '../../types';
import ChooseTargetStep from './convert-to-verification/ChooseTargetStep';
import ConvertPreviewStep from './convert-to-verification/ConvertPreviewStep';
import ConvertSuccessPanel from './convert-to-verification/ConvertSuccessPanel';
import ConvertWizardFooter from './convert-to-verification/ConvertWizardFooter';
import SelectFailureModesStep from './convert-to-verification/SelectFailureModesStep';
import { useConvertWizard } from './convert-to-verification/useConvertWizard';

interface ConvertToVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  drbfmSessionId: string;
  drbfmItemName: string;
  failureModes: FailureMode[];
  designChanges: DesignChange[];
  countermeasures: Countermeasure[];
}

const STEP_KEYS = [
  'selectFailureModes',
  'chooseTarget',
  'preview',
] as const;

const ConvertToVerificationDialog: React.FC<
  ConvertToVerificationDialogProps
> = ({
  open,
  onClose,
  drbfmSessionId,
  drbfmItemName,
  failureModes,
  designChanges,
  countermeasures,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const wizard = useConvertWizard({
    open,
    onClose,
    drbfmSessionId,
    drbfmItemName,
    failureModes,
    designChanges,
    countermeasures,
  });

  const steps = useMemo(
    () => STEP_KEYS.map((stepKey) => ({
      id: stepKey,
      label: t(`drbfm.convert.steps.${stepKey}`),
    })),
    [t],
  );

  const { isSubmitting, success, error, activeStep } = wizard;

  const handleDismiss = async () => {
    if (isSubmitting) return;
    if (wizard.isDirty && !(await confirm({
      title: t('confirmation.unsavedChangesTitle'),
      description: t('confirmation.unsavedChanges'),
      destructive: false,
    }))) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void handleDismiss();
      }}
    >
      {/*
        The inset keeps the MUI dialog's viewport gutter, which `w-full` alone
        drops. Hiding the built-in close while submitting matches the MUI header,
        which dropped its close button for the duration of the request.
      */}
      <DialogContent
        className={cn(
          'revy-ui grid-rows-[auto_minmax(0,1fr)_auto] gap-0 max-h-[calc(100vh-4rem)] w-[calc(100%-2rem)] p-0 sm:max-w-[900px]',
          success ? 'min-h-[300px]' : 'min-h-[550px]',
          isSubmitting && '[&>button]:hidden',
        )}
        aria-describedby={undefined}
      >
        <DialogHeader className="p-6">
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="size-5 text-primary" strokeWidth={1.9} />
            <span className="revy-cjk text-lg leading-[1.5]">
              {t('drbfm.convert.dialogTitle', '検証項目に変換')}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto border-y p-6">
          {success ? (
            <ConvertSuccessPanel
              itemsCount={wizard.resultItemsCount}
              onClose={onClose}
              onNavigateToVerification={wizard.handleNavigateToVerification}
            />
          ) : (
            <>
              <Stepper
                steps={steps}
                activeStep={activeStep}
                className="mb-6"
                progressLabel={(current, total) => `${current} / ${total}`}
              />

              {error && (
                <Alert variant="destructive" className="mb-4 flex items-start gap-2">
                  <AlertDescription className="revy-cjk flex-1 leading-[1.6]">
                    {error}
                  </AlertDescription>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    // -my-1: the 32px button would otherwise set the row height and
                    // leave a 22px line of text sitting above centre.
                    className="-my-1"
                    aria-label={t('common.close')}
                    onClick={() => wizard.setError(null)}
                  >
                    <X strokeWidth={1.9} />
                  </Button>
                </Alert>
              )}

              {activeStep === 0 && (
                <SelectFailureModesStep
                  sortedFMs={wizard.sortedFMs}
                  selectedFMIds={wizard.selectedFMIds}
                  totalCount={failureModes.length}
                  onToggle={wizard.handleToggleFM}
                  onSelectAll={wizard.handleSelectAll}
                  onDeselectAll={wizard.handleDeselectAll}
                />
              )}
              {activeStep === 1 && (
                <ChooseTargetStep wizard={wizard} drbfmItemName={drbfmItemName} />
              )}
              {activeStep === 2 && (
                <ConvertPreviewStep
                  previewItems={wizard.previewItems}
                  targetMode={wizard.targetMode}
                  newSessionTitle={wizard.newSessionTitle}
                  existingSessions={wizard.existingSessions}
                  selectedSessionId={wizard.selectedSessionId}
                />
              )}
            </>
          )}
        </div>

        {!success && (
          <ConvertWizardFooter
            activeStep={activeStep}
            isSubmitting={isSubmitting}
            canProceedStep1={wizard.canProceedStep1}
            canProceedStep2={wizard.canProceedStep2}
            onClose={onClose}
            onBack={wizard.handleBack}
            onNext={wizard.handleNext}
            onSubmit={wizard.handleSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToVerificationDialog;
